import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { api } from '../lib/api';
import { Scale, ExternalLink, Loader2, Crown, TrendingDown } from 'lucide-react';

const STATUS_STYLE = {
  LIVE: 'text-emerald-600 border-emerald-500/40',
  DEMO: 'text-accent border-accent/40',
  INDICATIVE: 'text-amber-600 border-amber-500/40',
};
const RANK = { LIVE: 3, INDICATIVE: 2, DEMO: 1 };

// Side-by-side provider price compare for a canonical FlightDeals product (fd_id).
// Highlights the CHEAPEST price and the BEST VALUE (prefers a LIVE/verified price, then cheapest).
export const PriceCompare = ({ fdId, label = 'Compare providers', trigger = null }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.getProduct(fdId);
      setData(data);
    } catch (e) { setData({ error: true }); }
    finally { setLoading(false); }
  };

  const onOpenChange = (v) => { setOpen(v); if (v && !data) load(); };

  // Build one comparable row per provider mapping, joined with its latest price.
  const rows = React.useMemo(() => {
    if (!data || data.error) return [];
    const prices = data.prices || [];
    const byProvider = {};
    prices.forEach((p) => { byProvider[p.provider] = p; });
    const out = (data.provider_mappings || []).map((m) => {
      const pr = byProvider[m.provider] || {};
      return {
        provider: m.provider || 'provider',
        provider_product_id: m.provider_product_id,
        deep_link: m.deep_link,
        offer_id: m.offer_id || pr.offer_id,
        price: pr.price != null ? pr.price : null,
        currency: pr.currency || data.best_price_currency || 'INR',
        status: pr.status || 'DEMO',
      };
    });
    // Providers that only exist as a price row (no mapping) — include them too.
    prices.forEach((p) => {
      if (!out.some((r) => r.provider === p.provider)) {
        out.push({ provider: p.provider, price: p.price, currency: p.currency, status: p.status });
      }
    });
    return out;
  }, [data]);

  const priced = rows.filter((r) => r.price != null);
  const cheapest = priced.length ? Math.min(...priced.map((r) => r.price)) : null;
  const bestValue = priced.length
    ? [...priced].sort((a, b) => (RANK[b.status] - RANK[a.status]) || (a.price - b.price))[0]
    : null;

  const go = (r) => {
    const vertical = data?.vertical || 'experience';
    const oid = r.offer_id;
    if (!oid) { if (r.deep_link) window.open(r.deep_link, '_blank'); return; }
    window.open(api.goVerticalUrl(vertical, oid, { placement: 'price_compare' }), '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="rounded-full text-xs h-7" data-testid={`compare-${fdId}`}>
            <Scale className="h-3.5 w-3.5 mr-1" /> {label}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="price-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Scale className="h-4 w-4 text-accent" /> Price comparison</DialogTitle>
        </DialogHeader>
        {loading && <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-accent" /></div>}
        {!loading && data && !data.error && (
          <div className="space-y-3">
            <p className="text-sm font-medium truncate">{data.title}</p>
            {rows.length === 0 && <p className="text-sm text-muted-foreground">No providers connected for this product yet.</p>}
            <div className="space-y-2" data-testid="compare-rows">
              {rows.map((r, i) => {
                const isCheapest = r.price != null && r.price === cheapest;
                const isBest = bestValue && r.provider === bestValue.provider && r.price === bestValue.price;
                return (
                  <div key={i} className={`rounded-xl border p-3 flex items-center justify-between gap-3 ${isBest ? 'border-accent/50 bg-accent/5' : 'border-border'}`} data-testid={`compare-row-${r.provider}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-medium capitalize truncate">{String(r.provider).replace(/[_:]/g, ' ')}</span>
                        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${STATUS_STYLE[r.status] || 'text-muted-foreground border-border'}`}>{r.status}</span>
                        {isCheapest && <Badge className="h-5 gap-0.5 bg-emerald-500/12 text-emerald-600 border-emerald-500/30" variant="outline"><TrendingDown className="h-3 w-3" /> Cheapest</Badge>}
                        {isBest && <Badge className="h-5 gap-0.5 bg-accent/12 text-accent border-accent/30" variant="outline"><Crown className="h-3 w-3" /> Best value</Badge>}
                      </div>
                      {r.provider_product_id && <span className="block text-[11px] text-muted-foreground truncate">ID: {r.provider_product_id}</span>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm font-semibold tabular-nums">{r.price != null ? `${r.currency} ${Math.round(r.price)}` : '—'}</span>
                      {(r.deep_link || (r.offer_id && !String(r.offer_id).startsWith('cmp_'))) && (
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => go(r)} data-testid={`compare-go-${r.provider}`}><ExternalLink className="h-3.5 w-3.5" /></Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground">Prices marked DEMO/INDICATIVE are examples until the provider is connected for live pricing.</p>
          </div>
        )}
        {!loading && data?.error && <p className="text-sm text-muted-foreground py-6 text-center">Couldn't load comparison right now.</p>}
      </DialogContent>
    </Dialog>
  );
};
